import { Modal, ModalDialog, DialogContent, DialogTitle, FormControl, FormLabel, Input, Checkbox, DialogActions, Button, Box, Typography, CircularProgress } from "@mui/joy";
import React from "react";
import WrappedButton from "../WrappedButton";

interface SignatureDialogProps {
	proposalId: string;
	estimateNum: string;
	firstName: string;
	lastName: string;
	open: boolean;
	onClose: () => void;
	onApproved: () => void;
}

/**
 * Dialog component for signing and approving proposals
 * Includes a signature canvas, name input, and auto-sign option
 */
/**
 * Generates a new GUID/UUID for signature reference
 */
const generateGuid = (): string => {
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		const v = c === "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
};

export default function SignatureDialog(props: SignatureDialogProps) {
	const canvasRef = React.useRef<HTMLCanvasElement>(null);
	const signatureBoxRef = React.useRef<HTMLDivElement>(null);
	const [isDrawing, setIsDrawing] = React.useState(false);
	const [firstName, setFirstName] = React.useState(props.firstName);
	const [lastName, setLastName] = React.useState(props.lastName);
	const [autoSign, setAutoSign] = React.useState(false);
	const [saving, setSaving] = React.useState(false);
	const [lastPos, setLastPos] = React.useState({ x: 0, y: 0 });
	const [signatureId, setSignatureId] = React.useState("");
	const [pdfPreview, setPdfPreview] = React.useState<string | null>(null);
	const [loadingPdf, setLoadingPdf] = React.useState(false);
	const [signatureMode, setSignatureMode] = React.useState<"waiting" | "signing">("waiting");
	const [agreeElectronicSignature, setAgreeElectronicSignature] = React.useState(false);
	const [agreeTermsConditions, setAgreeTermsConditions] = React.useState(false);

	// Initialize canvas and auto-populate name when dialog opens
	React.useEffect(() => {
		if (props.open) {
			setFirstName(props.firstName);
			setLastName(props.lastName);
			setAutoSign(false);
			setSignatureMode("waiting");
			setAgreeElectronicSignature(false);
			setAgreeTermsConditions(false);
			// Generate a new signature ID each time the dialog opens
			setSignatureId(generateGuid());
			// Clear canvas when dialog opens
			setTimeout(() => clearCanvas(), 100);
			// Fetch PDF preview
			fetchPdfPreview();
		}
	}, [props.open, props.firstName, props.lastName]);

	/**
	 * Fetches the PDF preview image from the backend
	 */
	const fetchPdfPreview = () => {
		setLoadingPdf(true);
		setPdfPreview(null);
		fetch("Proposal/GetPdfPreview", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ estimateNum: props.estimateNum }),
		})
			.then((x) => x.json())
			.then((data) => {
				setPdfPreview(data.image);
			})
			.catch((error) => {
				console.error("Error fetching PDF preview:", error);
			})
			.finally(() => {
				setLoadingPdf(false);
			});
	};

	// Combined full name for display and auto-sign
	const fullName = `${firstName || ""} ${lastName || ""}`.trim();

	// Handle auto-sign checkbox toggle - only if in signing mode
	React.useEffect(() => {
		if (signatureMode === "signing") {
		if (autoSign && fullName) {
			drawCursiveSignature(fullName);
		} else if (!autoSign) {
			clearCanvas();
		}
		}
	}, [autoSign, fullName, signatureMode]);

	/**
	 * Clears the signature canvas
	 * NOTE: Change "#90EE90" to "white" for production (green is for debugging canvas position)
	 */
	const clearCanvas = () => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;
		ctx.fillStyle = "white"; // Green for debugging - change to "white" for production
		ctx.fillRect(0, 0, canvas.width, canvas.height);
	};

	/**
	 * Draws the user's name in a cursive style on the canvas
	 * NOTE: Change "#90EE90" to "white" for production (green is for debugging canvas position)
	 */
	const drawCursiveSignature = (name: string) => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		// Clear canvas first
		ctx.fillStyle = "white"; // Green for debugging - change to "white" for production
		ctx.fillRect(0, 0, canvas.width, canvas.height);

		// Draw cursive signature
		ctx.font = "italic 48px 'Brush Script MT', cursive";
		ctx.fillStyle = "#000080";
		ctx.textAlign = "center";
		ctx.textBaseline = "middle";
		ctx.fillText(name, canvas.width / 2, canvas.height / 2);
	};

	/**
	 * Gets the mouse/touch position relative to the canvas
	 */
	const getPosition = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
		const canvas = canvasRef.current;
		if (!canvas) return { x: 0, y: 0 };
		const rect = canvas.getBoundingClientRect();

		if ("touches" in e) {
			return {
				x: e.touches[0].clientX - rect.left,
				y: e.touches[0].clientY - rect.top,
			};
		}
		return {
			x: e.clientX - rect.left,
			y: e.clientY - rect.top,
		};
	};

	/**
	 * Handles the start of drawing on the canvas
	 */
	const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
		if (autoSign || signatureMode !== "signing") return;
		setIsDrawing(true);
		const pos = getPosition(e);
		setLastPos(pos);
	};

	/**
	 * Handles drawing on the canvas as the user moves
	 */
	const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
		if (!isDrawing || autoSign || signatureMode !== "signing") return;

		const canvas = canvasRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		const pos = getPosition(e);

		ctx.beginPath();
		ctx.strokeStyle = "#000080";
		ctx.lineWidth = 2;
		ctx.lineCap = "round";
		ctx.lineJoin = "round";
		ctx.moveTo(lastPos.x, lastPos.y);
		ctx.lineTo(pos.x, pos.y);
		ctx.stroke();

		setLastPos(pos);
	};

	/**
	 * Handles the end of drawing on the canvas
	 */
	const stopDrawing = () => {
		setIsDrawing(false);
	};

	/**
	 * Handles clicking the "Click to Sign" button
	 */
	const handleClickToSign = () => {
		setSignatureMode("signing");
		if (autoSign && fullName) {
			drawCursiveSignature(fullName);
		} else {
			clearCanvas();
		}
	};

	/**
	 * Scrolls to the signature box
	 */
	const scrollToSignature = () => {
		if (signatureBoxRef.current) {
			signatureBoxRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
		}
	};

	/**
	 * Submits the signature to the backend for approval
	 */
	const handleApprove = () => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		// Get signature as base64 image
		const signatureImage = canvas.toDataURL("image/png");

		setSaving(true);

		fetch("Proposal/SignEstimate", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				proposalId: props.proposalId,
				signatureImage: signatureImage,
				firstName: firstName,
				lastName: lastName,
				signatureId: signatureId,
				estimateNum: props.estimateNum,
			}),
		})
			.then((response) => {
				if (response.ok) {
					props.onApproved();
					props.onClose();
				} else {
					alert("Failed to save signature. Please try again.");
				}
			})
			.catch((error) => {
				console.error("Error saving signature:", error);
				alert("Failed to save signature. Please try again.");
			})
			.finally(() => {
				setSaving(false);
			});
	};

	return (
		<Modal open={props.open} onClose={props.onClose}>
			<ModalDialog sx={{ minWidth: 700, maxWidth: "90vw", maxHeight: "90vh", overflow: "auto" }}>
				<DialogTitle>Approve & Sign Proposal</DialogTitle>
				<DialogContent>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
						{/* Signature Reference ID */}
						<Box sx={{ backgroundColor: "neutral.100", p: 1.5, borderRadius: 1 }}>
							<Typography level="body-xs" sx={{ color: "neutral.600", mb: 0.5 }}>
								Signature Reference ID (save for your records)
							</Typography>
							<Typography level="body-md" sx={{ fontFamily: "monospace", fontWeight: "bold" }}>
								{signatureId}
							</Typography>
						</Box>

						{/* First name input field */}
						<FormControl>
							<FormLabel>First Name</FormLabel>
							<Input
								disabled
								value={firstName}
								onChange={(e) => setFirstName(e.target.value)}
								placeholder="Enter your first name"
							/>
						</FormControl>

						{/* Last name input field */}
						<FormControl>
							<FormLabel>Last Name</FormLabel>
							<Input
								disabled
								value={lastName}
								onChange={(e) => setLastName(e.target.value)}
								placeholder="Enter your last name"
							/>
						</FormControl>

						{/* Auto-sign checkbox */}
						<Checkbox
							label="Auto-sign with my name in cursive"
							checked={autoSign}
							onChange={(e) => setAutoSign(e.target.checked)}
						/>

						{/* Scroll to signature button */}
						{pdfPreview && signatureMode === "waiting" && (
							<Button variant="solid" color="primary" onClick={scrollToSignature} fullWidth>
								↓ Scroll to Signature Location ↓
							</Button>
						)}

						{/* PDF Preview with signature canvas overlay */}
						<Box>
							<Typography level="body-sm" sx={{ mb: 1 }}>
								Review the document below and sign at the designated location
							</Typography>
							<Box
								sx={{
									border: "1px solid",
									borderColor: "neutral.300",
									borderRadius: 1,
									overflow: "auto",
									position: "relative",
									maxHeight: "60vh",
									backgroundColor: "neutral.200",
								}}>
								{/* Loading spinner */}
								{loadingPdf && (
									<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
										<CircularProgress />
									</Box>
								)}

								{/* PDF Preview image */}
								{pdfPreview && (
									<Box sx={{ position: "relative" }}>
										<img
											src={`data:image/png;base64,${pdfPreview}`}
											alt="Proposal Preview"
											style={{ width: "100%", display: "block" }}
										/>

										{/* Signature box - positioned near bottom */}
										<Box
											ref={signatureBoxRef}
											sx={{
												position: "absolute",
												left: "260px",
												top: "780px",
												transform: "translateX(-50%)",
												width: "300px",
												display: "flex",
												flexDirection: "column",
												alignItems: "center",
												gap: 1,
											}}>
											{signatureMode === "waiting" ? (
												<>
													<Button
														variant="solid"
														color="warning"
														size="lg"
														onClick={handleClickToSign}
														fullWidth
														sx={{
															animation: "pulse 2s infinite",
															"@keyframes pulse": {
																"0%, 100%": { transform: "scale(1)" },
																"50%": { transform: "scale(1.05)" },
															},
														}}>
														👉 Click Here to Sign 👈
													</Button>
													<Typography level="body-xs" sx={{ color: "warning.500", fontWeight: "bold" }}>
														↑ Signature Required ↑
													</Typography>
												</>
											) : (
												<>
													<canvas
														ref={canvasRef}
														width={300}
														height={80}
														style={{
															display: "block",
															cursor: autoSign ? "default" : "crosshair",
															touchAction: "none",
															border: "2px dashed #666",
															backgroundColor: "white",
														}}
														onMouseDown={startDrawing}
														onMouseMove={draw}
														onMouseUp={stopDrawing}
														onMouseLeave={stopDrawing}
														onTouchStart={startDrawing}
														onTouchMove={draw}
														onTouchEnd={stopDrawing}
													/>
													<Typography level="body-xs" sx={{ color: "neutral.600" }}>
														{autoSign ? "Signature applied" : "Draw your signature above"}
													</Typography>
													{!autoSign && (
														<Button variant="outlined" color="neutral" onClick={clearCanvas} size="sm">
															Clear Signature
														</Button>
													)}
												</>
											)}
										</Box>
									</Box>
								)}
							</Box>
						</Box>

							{/* Terms and Conditions Checkboxes */}
							<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 2, p: 2, backgroundColor: "neutral.50", borderRadius: 1 }}>
								<Checkbox
									label={
										<Typography level="body-sm">
											By selecting Adopt and Sign, I agree that the signature and initials will be the electronic representation of my signature and initials for all purposes when I (or my agent) use them on documents, including legally binding contracts.
										</Typography>
									}
									checked={agreeElectronicSignature}
									onChange={(e) => setAgreeElectronicSignature(e.target.checked)}
								/>
								<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
									<Checkbox
										checked={agreeTermsConditions}
										onChange={(e) => setAgreeTermsConditions(e.target.checked)}
										sx={{ mt: 0.25 }}
									/>
									<Typography level="body-sm" sx={{ flex: 1 }}>
										I agree with all the{" "}
										<a 
											href="https://www.dynamicelevatorcontrol.com/terms-conditions/" 
											target="_blank" 
											rel="noopener noreferrer" 
											style={{ color: "inherit", textDecoration: "underline", cursor: "pointer" }}
										>
											terms and conditions
										</a>
										.
									</Typography>
								</Box>
							</Box>
						</Box>
					</DialogContent>
					<DialogActions>
						<WrappedButton busy={saving} onClick={handleApprove} color="primary" disabled={signatureMode === "waiting" || !agreeElectronicSignature || !agreeTermsConditions}>
							Adopt & Sign
						</WrappedButton>
						<Button variant="outlined" color="neutral" onClick={props.onClose}>
							Cancel
						</Button>
					</DialogActions>
				</ModalDialog>
			</Modal>
		);
	}
